import type { SessionResponse } from '@chatapp-inbox/shared';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { fetchSession, setCsrfToken, setInvalidCsrfHandler } from '../api';
import type { FlagsStore } from './flags-store';
import { endSession, startSession } from './session-actions';
import { SESSION_KEY } from './session';

/**
 * Tempo mínimo entre duas checagens disparadas por foco/visibilidade. Voltar a uma aba dispara
 * `visibilitychange`, `focus` (e `pageshow`) quase juntos, e quem alterna de aba o tempo todo não
 * deve gerar um pedido a cada volta.
 */
const MIN_CHECK_INTERVAL_MS = 5_000;

const readSession = (queryClient: QueryClient): SessionResponse | null =>
  queryClient.getQueryData<SessionResponse | null>(SESSION_KEY) ?? null;

/**
 * Confere com o servidor se a sessão desta aba ainda é a do cookie e acerta o que for diferente.
 *
 * O cookie de sessão é UM por navegador, mas cada aba guarda a própria identidade e o próprio token
 * CSRF em memória. Outra aba pode ter saído, entrado de novo ou trocado de conta, e esta aba
 * continuaria mostrando (e enviando pedidos como) a conta antiga.
 */
async function reconcileSession(
  queryClient: QueryClient,
  store: FlagsStore,
  signal: AbortSignal,
): Promise<void> {
  const before = readSession(queryClient);
  if (!before) return; // sem sessão (entrando, saindo, erro): não há o que reconciliar

  let fresh: SessionResponse | null;
  try {
    // Já guarda o token CSRF novo (ou esquece o antigo, se o servidor respondeu 401).
    fresh = await fetchSession(signal);
  } catch {
    // Sem rede, 5xx, tempo esgotado ou aba fechada: não ficamos sabendo de nada novo. A sessão
    // continua como está e a próxima volta (ou o próximo 403) tenta de novo.
    return;
  }

  const current = readSession(queryClient);
  if (current !== before || store.get().loggingOut) {
    // Enquanto perguntávamos algo encerrou ou trocou a sessão (login, logout, 401), ou a pessoa
    // está saindo: a resposta é velha e não pode mexer na tela. O `fetchSession` porém já trocou o
    // token, então devolve o que vale para o estado atual (o do cache).
    setCsrfToken(current?.csrfToken ?? null);
    return;
  }

  if (fresh === null) {
    // O cookie não vale mais (saiu em outra aba, ou a sessão expirou): mesmo caminho do 401.
    endSession(queryClient, store, 'expired');
  } else if (fresh.user.id !== before.user.id) {
    // Outra pessoa assumiu o cookie: nada do usuário anterior pode ficar na tela nem no cache.
    startSession(queryClient, store, fresh);
  } else {
    // A mesma pessoa, talvez com token novo (entrou de novo em outra aba) ou papel alterado.
    queryClient.setQueryData(SESSION_KEY, fresh);
  }
}

/**
 * Mantém a sessão desta aba alinhada ao cookie compartilhado entre abas:
 * - ao voltar a aba (visibilidade, foco, restauração do cache de navegação), no máximo a cada 5 s
 *   e nunca durante um logout;
 * - quando um pedido recebe 403 `invalid_csrf`, na hora (esse caminho ignora o intervalo mínimo e
 *   não repete o pedido: se a conta mudou, repeti-lo agiria como OUTRA pessoa).
 */
export function useSessionReconciliation(store: FlagsStore): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    const abort = new AbortController();
    let inFlight = false;
    let lastCheckAt = Number.NEGATIVE_INFINITY;

    const check = (force: boolean): void => {
      if (inFlight) return;
      if (!force) {
        if (store.get().loggingOut) return;
        // A sessão acabou de ser lida ou adotada (carga, login, checagem anterior): já está fresca.
        const learnedAt = queryClient.getQueryState(SESSION_KEY)?.dataUpdatedAt ?? 0;
        if (Date.now() - Math.max(lastCheckAt, learnedAt) < MIN_CHECK_INTERVAL_MS) return;
      }
      // No 403 do próprio logout `loggingOut` ainda é true neste instante (o erro do pedido ainda
      // não chegou ao tratador), por isso o caminho forçado não olha para ele aqui: a conferência
      // vale quando a RESPOSTA chega, e `reconcileSession` a descarta se ainda estiver saindo.
      inFlight = true;
      lastCheckAt = Date.now();
      void reconcileSession(queryClient, store, abort.signal).finally(() => {
        inFlight = false;
      });
    };

    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') check(false);
    };
    const onFocus = (): void => check(false);
    const onPageShow = (event: PageTransitionEvent): void => {
      // `persisted`: página restaurada do cache de navegação (botão voltar), com a memória de antes.
      if (event.persisted) check(false);
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', onFocus);
    window.addEventListener('pageshow', onPageShow);
    setInvalidCsrfHandler(() => check(true));
    // O React em modo estrito monta, desmonta e monta de novo: o cleanup precisa desfazer
    // exatamente o que o efeito fez para o segundo registro valer.
    return () => {
      abort.abort();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('pageshow', onPageShow);
      setInvalidCsrfHandler(null);
    };
  }, [queryClient, store]);
}
